import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  SelectChangeEvent,
  Chip,
  Grid,
  InputAdornment,
  Autocomplete // For AD user simulation
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Or use VisibilityOff for Deactivate
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';

// --- Interfaces ---
interface User {
  id: string;
  username: string;
  fullName: string;
  email: string;
  department: string;
  role: string;
  status: 'Active' | 'Inactive';
  creationDate: Date;
  lastUpdatedDate: Date;
  createdBy: string;
  lastUpdatedBy: string;
}

// --- Mock Data ---
// Simulate AD Users that can be searched
const mockADUsers = [
  { username: 'j.doe', fullName: 'John Doe', email: 'john.doe@example.com', department: 'Finance' },
  { username: 'a.smith', fullName: 'Alice Smith', email: 'alice.smith@example.com', department: 'IT' },
  { username: 'b.jones', fullName: 'Bob Jones', email: 'bob.jones@example.com', department: 'HR' },
  { username: 'c.williams', fullName: 'Charlie Williams', email: 'charlie.williams@example.com', department: 'Operations' },
  { username: 'd.brown', fullName: 'David Brown', email: 'david.brown@example.com', department: 'Finance' },
];

const initialUsers: User[] = [
  {
    id: 'USR001',
    username: 'admin.user',
    fullName: 'Admin User',
    email: 'admin@example.com',
    department: 'IT',
    role: 'Back-end Administrator',
    status: 'Active',
    creationDate: new Date(2023, 0, 15),
    lastUpdatedDate: new Date(2023, 5, 20),
    createdBy: 'System',
    lastUpdatedBy: 'System',
  },
  {
    id: 'USR002',
    username: 'manager.bob',
    fullName: 'Bob Manager',
    email: 'bob.m@example.com',
    department: 'Finance',
    role: 'Business Manager',
    status: 'Active',
    creationDate: new Date(2023, 1, 10),
    lastUpdatedDate: new Date(2023, 8, 1),
    createdBy: 'admin.user',
    lastUpdatedBy: 'admin.user',
  },
  {
    id: 'USR003',
    username: 'emp.alice',
    fullName: 'Alice Employee',
    email: 'alice.e@example.com',
    department: 'Operations',
    role: 'Employee',
    status: 'Inactive',
    creationDate: new Date(2023, 2, 5),
    lastUpdatedDate: new Date(2023, 6, 15),
    createdBy: 'manager.bob',
    lastUpdatedBy: 'manager.bob',
  },
];

const roles = [
  'Back-end Administrator',
  'Business Administrator',
  'Business Manager',
  'Employee',
  'CEO',
  'Head of Department'
];
const statuses = ['Active', 'Inactive'];

// --- Component ---
const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | 'view'>('add');
  const [currentUser, setCurrentUser] = useState<Partial<User> | null>(null);
  const [selectedADUser, setSelectedADUser] = useState<typeof mockADUsers[0] | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');

  // Derived state for filtered users
  const filteredUsers = users.filter(user =>
    (user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.fullName.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (filterRole === '' || user.role === filterRole)
  );

  const handleOpenDialog = (mode: 'add' | 'edit' | 'view', user?: User) => {
    setDialogMode(mode);
    if (mode === 'add') {
      setCurrentUser({});
      setSelectedADUser(null); // Reset AD selection
    } else if (user) {
      setCurrentUser({ ...user });
      // Simulate finding the AD user based on existing username for view/edit
      const adUser = mockADUsers.find(ad => ad.username === user.username);
      setSelectedADUser(adUser || null); 
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentUser(null);
    setSelectedADUser(null);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setCurrentUser(prev => (prev ? { ...prev, [name]: value } : null));
  };

  const handleADUserChange = (event: any, newValue: typeof mockADUsers[0] | null) => {
    setSelectedADUser(newValue);
    setCurrentUser(prev => ({
        ...(prev || {}),
        username: newValue?.username || '',
        fullName: newValue?.fullName || '',
        email: newValue?.email || '',
        department: newValue?.department || '',
    }));
  };

  const handleSave = () => {
    if (!currentUser) return;

    const now = new Date();
    const loggedInUser = 'current.user'; // Simulate logged-in user

    if (dialogMode === 'add') {
      if (!selectedADUser) {
          alert("Please select a user from the directory."); // Basic validation
          return;
      }
      const newUser: User = {
        id: `USR${String(users.length + 1).padStart(3, '0')}`,
        username: selectedADUser.username,
        fullName: selectedADUser.fullName,
        email: selectedADUser.email,
        department: selectedADUser.department,
        role: currentUser.role || roles[0], // Default or validation needed
        status: currentUser.status || 'Active', // Default or validation needed
        creationDate: now,
        lastUpdatedDate: now,
        createdBy: loggedInUser,
        lastUpdatedBy: loggedInUser,
      };
      setUsers([...users, newUser]);
    } else if (dialogMode === 'edit' && currentUser.id) {
      setUsers(users.map(user =>
        user.id === currentUser.id
          ? { ...user, ...currentUser, lastUpdatedDate: now, lastUpdatedBy: loggedInUser } as User
          : user
      ));
    }
    handleCloseDialog();
  };

  const handleDeactivate = (userId: string) => {
    setUsers(users.map(user =>
      user.id === userId
        ? { ...user, status: 'Inactive', lastUpdatedDate: new Date(), lastUpdatedBy: 'current.user' }
        : user
    ));
     console.log(`Deactivated user ${userId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <Typography color="text.primary">Administration</Typography>
        <Typography color="text.primary">User Management</Typography>
      </Breadcrumbs>

      {/* Header and Filters */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid #E5E7EB', borderRadius: 2, bgcolor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search by Username or Full Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filterRole}
                label="Role"
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
              sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' }, textTransform: 'none', px: 3 }}
            >
              Add New User
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* User Table */}
      <TableContainer component={Paper} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#F9FAFB', fontWeight: 'bold' } }}>
              <TableCell>User ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.fullName}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  <Chip
                    label={user.status}
                    color={user.status === 'Active' ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenDialog('view', user)} title="View">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleOpenDialog('edit', user)} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {user.status === 'Active' && (
                    <IconButton size="small" onClick={() => handleDeactivate(user.id)} title="Deactivate">
                      <DeleteIcon fontSize="small" color="error"/>
                      {/* Or use <VisibilityOffIcon fontSize="small" color="warning" /> */}
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit/View Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'add' ? 'Add New User' : dialogMode === 'edit' ? 'Edit User' : 'View User Details'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialogMode !== 'add' && (
              <Grid item xs={12} sm={6}>
                <TextField label="User ID" value={currentUser?.id || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
              </Grid>
            )}
            
            {dialogMode === 'add' ? (
                <Grid item xs={12} sm={dialogMode === 'add' ? 12 : 6}>
                    <Autocomplete
                        options={mockADUsers}
                        getOptionLabel={(option) => `${option.fullName} (${option.username})`}
                        value={selectedADUser}
                        onChange={handleADUserChange}
                        isOptionEqualToValue={(option, value) => option.username === value.username}
                        renderInput={(params) => 
                            <TextField {...params} label="Search User Directory (Username/Full Name)" required />
                        }
                    />
              </Grid>
            ) : (
                 <Grid item xs={12} sm={6}>
                    <TextField label="Username" name="username" value={currentUser?.username || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                 </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField label="Full Name" name="fullName" value={currentUser?.fullName || ''} fullWidth disabled={dialogMode !== 'add'} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" name="email" value={currentUser?.email || ''} fullWidth disabled={dialogMode !== 'add'} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Department" name="department" value={currentUser?.department || ''} fullWidth disabled={dialogMode !== 'add'} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required={dialogMode !== 'view'}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  name="role"
                  value={currentUser?.role || ''}
                  onChange={handleInputChange}
                  disabled={dialogMode === 'view'}
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required={dialogMode !== 'view'}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  name="status"
                  value={currentUser?.status || ''}
                  onChange={handleInputChange}
                  disabled={dialogMode === 'view'}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {dialogMode !== 'add' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Creation Date" value={currentUser?.creationDate ? format(currentUser.creationDate, 'P p') : ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Created By" value={currentUser?.createdBy || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Last Updated Date" value={currentUser?.lastUpdatedDate ? format(currentUser.lastUpdatedDate, 'P p') : ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Last Updated By" value={currentUser?.lastUpdatedBy || ''} fullWidth disabled InputLabelProps={{ shrink: true }} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#6B7280' }}>Cancel</Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}>Save</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 